import React, { useState, useEffect, useContext } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  TablePagination,
  Collapse,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import RefreshIcon from '@material-ui/icons/Refresh';
import axios from 'axios';
import Skeleton from '@material-ui/lab/Skeleton';
import { AuthContext } from '../../../@jumbo/components/context/AuthContext';
import RefreshToken from '../../partials/RefreshToken';
import PageContainer from '../../../@jumbo/components/PageComponents/layouts/PageContainer';
import GridContainer from '../../../@jumbo/components/GridContainer';
import Grid from '@material-ui/core/Grid';

const STATUS_LABELS = {
  SubmittedForProcessing: 'Submitted',
  AwaitingAgentConfirmation: 'Awaiting Confirmation',
  DeclinedByAgent: 'Declined',
  UnderProcessing: 'Processing',
  ReadyForWithdrawal: 'Ready for Withdrawal',
  LockedForWithdrawal: 'Locked',
  Withdrawn: 'Completed',
  Failed: 'Failed',
};

const COLLECTION_POINT_LABELS = {
  BANK: 'Bank Transfer',
  MFS: 'Mobile Money',
  AGENT: 'Cash Pickup',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const formatAmount = (num) => {
  if (num == null) return '0.00';
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Recipient display for table: use recipientName, or derive from receiver objects
const getRecipientDisplay = (row) => {
  if (row.recipientName) return row.recipientName;
  if (row.collectionPoint === 'AGENT' && row.receiver?.fullName) return row.receiver.fullName;
  if (row.collectionPoint === 'BANK' && row.receiverBankAccount?.accountName) return row.receiverBankAccount.accountName;
  if (row.collectionPoint === 'MFS' && row.receiverMfsAccount?.accountName) return row.receiverMfsAccount.accountName;
  return '-';
};

// Receiver detail content based on collectionPoint
const ReceiverDetail = ({ row }) => {
  if (row.collectionPoint === 'AGENT' && row.receiver) {
    return (
      <>
        <Typography variant="body2">Name: {row.receiver.fullName || '-'}</Typography>
        <Typography variant="body2">Mobile: {row.receiver.mobileNumber || '-'}</Typography>
        <Typography variant="body2">Document: {row.receiver.documentType || '-'} - {row.receiver.documentNumber || '-'}</Typography>
      </>
    );
  }
  if (row.collectionPoint === 'BANK' && row.receiverBankAccount) {
    return (
      <>
        <Typography variant="body2">Account Name: {row.receiverBankAccount.accountName || '-'}</Typography>
        <Typography variant="body2">Account Number: {row.receiverBankAccount.accountNumber || '-'}</Typography>
        <Typography variant="body2">Bank: {row.receiverBankAccount.bankName || '-'}</Typography>
        <Typography variant="body2">Branch: {row.receiverBankAccount.branchName || '-'}</Typography>
      </>
    );
  }
  if (row.collectionPoint === 'MFS' && row.receiverMfsAccount) {
    return (
      <>
        <Typography variant="body2">Account Name: {row.receiverMfsAccount.accountName || '-'}</Typography>
        <Typography variant="body2">Account Number: {row.receiverMfsAccount.accountNumber || '-'}</Typography>
        <Typography variant="body2">MFS: {row.receiverMfsAccount.mfsName || '-'}</Typography>
      </>
    );
  }
  return <Typography variant="body2">No receiver details available.</Typography>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  container: {
    overflow: 'visible',
  },
  tableHeadStyle: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.875rem',
      backgroundColor: '#000132',
      color: 'white',
    },
  },
  tableBodyStyle: {
    '& td': {
      padding: '8px 16px',
    },
  },
  detailRow: {
    display: 'flex',
    width: '100%',
  },
  detailSection: {
    padding: theme.spacing(2),
    flex: 1,
  },
  senderBlock: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  receiverBlock: {
    backgroundColor: 'rgba(76, 175, 80, 0.08)',
  },
  transactionBlock: {
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
  },
  detailLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

const Transfers = () => {
  const classes = useStyles();
  const { glob_pay_admin_auth, logout, login } = useContext(AuthContext);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTransfers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await RefreshToken(glob_pay_admin_auth, logout, login);
      const url = `${process.env.REACT_APP_BASE_URL}/api/v1/private/admin/transfers?page=${page}&size=${perPage}`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setList(data.data.content || []);
      setTotalElements(data.data.totalElements || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load transfers');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [page, perPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedId(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const pageLoading = () =>
    Array.from({ length: Math.min(perPage, 5) }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: 9 }).map((_, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <PageContainer
      heading="All Transfers"
      headingAction={
        <IconButton onClick={fetchTransfers} disabled={loading} title="Refresh" size="small">
          <RefreshIcon />
        </IconButton>
      }
    >
      <GridContainer>
        <Grid item xs={12}>
          {error && (
            <Box mb={2} p={2} bgcolor="error.light" color="error.contrastText" borderRadius={1}>
              {error}
            </Box>
          )}
          <Paper>
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="transfers table" size="small">
                <TableHead className={classes.tableHeadStyle}>
                  <TableRow>
                    <TableCell padding="checkbox" style={{ backgroundColor: '#000132', color: 'white' }} />
                    <TableCell>Transaction #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sender</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Currencies</TableCell>
                    <TableCell>Collection Point</TableCell>
                    <TableCell>Countries</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBodyStyle}>
                  {!loading && list.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className={classes.emptyState}>
                        No transfers found.
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && pageLoading()}
                  {!loading &&
                    list.map((row) => (
                      <React.Fragment key={row.id}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <IconButton size="small" onClick={() => toggleExpand(row.id)}>
                              {expandedId === row.id ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.transactionNumber || '-'}</TableCell>
                          <TableCell>{STATUS_LABELS[row.status] || row.status}</TableCell>
                          <TableCell>
                            {row.sender?.fullName || (row.sender && (row.sender.mobileNumber || row.sender.address)) || 'N/A'}
                          </TableCell>
                          <TableCell>{getRecipientDisplay(row)}</TableCell>
                          <TableCell align="right">
                            {row.transaction
                              ? formatAmount(row.transaction.amountToTransfer)
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {row.transaction
                              ? `${row.transaction.fromCurrency} → ${row.transaction.toCurrency}`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {COLLECTION_POINT_LABELS[row.collectionPoint] || row.collectionPoint || '-'}
                          </TableCell>
                          <TableCell>
                            {row.originCountry && row.destinationCountry
                              ? `${row.originCountry} → ${row.destinationCountry}`
                              : '-'}
                          </TableCell>
                          <TableCell>{formatDate(row.createdAt)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                              <Box className={classes.detailRow}>
                                <Box className={`${classes.detailSection} ${classes.senderBlock}`}>
                                  <Typography className={classes.detailLabel}>Sender</Typography>
                                  {row.sender ? (
                                    <>
                                      <Typography variant="body2">Name: {row.sender.fullName || 'N/A'}</Typography>
                                      <Typography variant="body2">Address: {row.sender.address || '-'}</Typography>
                                      <Typography variant="body2">Mobile: {row.sender.mobileNumber || '-'}</Typography>
                                      <Typography variant="body2">Document: {(row.sender.documentType || '')} - {(row.sender.documentNumber || '-')}</Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2">-</Typography>
                                  )}
                                </Box>
                                <Box className={`${classes.detailSection} ${classes.receiverBlock}`}>
                                  <Typography className={classes.detailLabel}>Receiver</Typography>
                                  <ReceiverDetail row={row} />
                                </Box>
                                <Box className={`${classes.detailSection} ${classes.transactionBlock}`}>
                                  <Typography className={classes.detailLabel}>Transaction</Typography>
                                  {row.transaction ? (
                                    <>
                                      <Typography variant="body2">
                                        {row.transaction.fromCurrency} {formatAmount(row.transaction.amountToTransfer)} →{' '}
                                        {row.transaction.toCurrency} {formatAmount(row.transaction.amountReceivable)}
                                      </Typography>
                                      <Typography variant="body2">Charge: {formatAmount(row.transaction.charge)}</Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2">-</Typography>
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={perPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Rows per page:"
            />
          </Paper>
        </Grid>
      </GridContainer>
    </PageContainer>
  );
};

export default Transfers;
